import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react'
import { authApi, type TotpEnrollment } from '../../lib/api'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import { formatDatetime } from '../../lib/utils'
import CodeInput from '../../components/shared/CodeInput'
import RoleBadge from '../../components/shared/RoleBadge'
import { tx } from '../../lib/i18n'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorOf = (err: any, fallback: string): string => err?.response?.data?.error ?? fallback

const inputCls =
  'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500'

/** My account: sign-in security (authenticator app) + change password. */
const AccountPage: React.FC = () => {
  const { currentAdmin } = useAuth()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null)
  const [code, setCode] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })

  const { data: status, isLoading } = useQuery({
    queryKey: ['my-2fa'],
    queryFn: () => authApi.twoFactorStatus().then((r) => r.data.data!),
  })

  const setup = useMutation({
    mutationFn: () => authApi.twoFactorSetup().then((r) => r.data.data!),
    onSuccess: (d) => {
      setEnrollment(d)
      setCode('')
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, 'Could not start setup') }),
  })

  const enable = useMutation({
    mutationFn: () => authApi.twoFactorEnable(code),
    onSuccess: () => {
      setEnrollment(null)
      setCode('')
      queryClient.invalidateQueries({ queryKey: ['my-2fa'] })
      showToast({ type: 'success', message: 'เปิดการยืนยัน 2 ขั้นตอนแล้ว' })
    },
    onError: (e) => {
      setCode('')
      showToast({ type: 'error', message: errorOf(e, 'Invalid code') })
    },
  })

  const disable = useMutation({
    mutationFn: () => authApi.twoFactorDisable(disableCode),
    onSuccess: () => {
      setDisableCode('')
      queryClient.invalidateQueries({ queryKey: ['my-2fa'] })
      showToast({ type: 'success', message: 'ปิดการยืนยัน 2 ขั้นตอนแล้ว' })
    },
    onError: (e) => {
      setDisableCode('')
      showToast({ type: 'error', message: errorOf(e, 'Invalid code') })
    },
  })

  const changePw = useMutation({
    mutationFn: () => authApi.changePassword(pw.current, pw.next),
    onSuccess: () => {
      setPw({ current: '', next: '', confirm: '' })
      showToast({ type: 'success', message: 'เปลี่ยนรหัสผ่านแล้ว' })
    },
    onError: (e) => showToast({ type: 'error', message: errorOf(e, 'Could not change password') }),
  })

  const pwError =
    pw.next && pw.next.length < 8 ? 'อย่างน้อย 8 ตัวอักษร' : pw.confirm && pw.next !== pw.confirm ? 'รหัสผ่านใหม่ไม่ตรงกัน' : null

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('My Account')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">ความปลอดภัยของบัญชีคุณ</p>
      </div>

      {currentAdmin && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
            {currentAdmin.firstName?.[0]}
            {currentAdmin.lastName?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900">
              {currentAdmin.firstName} {currentAdmin.lastName}
            </p>
            <p className="text-sm text-gray-500 truncate">{currentAdmin.email}</p>
          </div>
          <RoleBadge role={currentAdmin.role} label={currentAdmin.roleLabel} />
        </div>
      )}

      {/* 2FA */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Smartphone className="h-4 w-4 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-gray-800">การยืนยัน 2 ขั้นตอน (Google Authenticator)</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              ตอนเข้าสู่ระบบ นอกจากรหัสผ่านต้องกรอกรหัส 6 หลักจากแอพ Google Authenticator / Microsoft Authenticator ด้วย
            </p>
          </div>
          {!isLoading && status && (
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                status.enabled ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {status.enabled ? 'เปิดอยู่' : 'ปิดอยู่'}
            </span>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-400">{tx('Loading…')}</p>
        ) : status?.enabled ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-indigo-600" />
              เปิดใช้ตั้งแต่ {status.enabledAt ? formatDatetime(status.enabledAt) : '-'}
            </p>
            {status.required ? (
              <p className="text-xs text-gray-400">ผู้ดูแลระบบบังคับให้ทุกคนใช้ 2 ขั้นตอน จึงปิดเองไม่ได้ (เปลี่ยนเครื่องให้ Super Admin กด Reset 2FA)</p>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2 border-t border-gray-100">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">กรอกรหัสจากแอพเพื่อปิด</label>
                  <CodeInput value={disableCode} onChange={setDisableCode} />
                </div>
                <button
                  onClick={() => disable.mutate()}
                  disabled={disable.isPending || disableCode.length !== 6}
                  className="flex items-center justify-center gap-2 px-4 py-3 border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  <ShieldOff className="h-4 w-4" /> ปิด 2 ขั้นตอน
                </button>
              </div>
            )}
          </div>
        ) : enrollment ? (
          <div className="grid md:grid-cols-2 gap-5 items-center">
            <div className="flex flex-col items-center gap-2">
              <img src={enrollment.qrDataUrl} alt="QR" className="w-52 h-52 rounded-lg border border-gray-100" />
              <p className="text-[11px] text-gray-400 text-center break-all">
                สแกนไม่ได้? กรอกรหัสนี้ในแอพ: <span className="font-mono text-gray-600">{enrollment.secret}</span>
              </p>
            </div>
            <div className="space-y-3">
              <ol className="text-sm text-gray-600 list-decimal pl-5 space-y-1">
                <li>ติดตั้ง Google Authenticator บนมือถือ</li>
                <li>กด + แล้วเลือก “สแกนคิวอาร์โค้ด”</li>
                <li>กรอกรหัส 6 หลักที่แอพแสดง</li>
              </ol>
              <CodeInput value={code} onChange={setCode} autoFocus />
              <div className="flex gap-2">
                <button
                  onClick={() => enable.mutate()}
                  disabled={enable.isPending || code.length !== 6}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {enable.isPending ? 'Verifying…' : 'ยืนยันและเปิดใช้'}
                </button>
                <button
                  onClick={() => setEnrollment(null)}
                  className="px-4 py-2 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50"
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setup.mutate()}
            disabled={setup.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <ShieldCheck className="h-4 w-4" /> {setup.isPending ? tx('Loading…') : 'เปิดการยืนยัน 2 ขั้นตอน'}
          </button>
        )}
      </div>

      {/* Password */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gray-100 rounded-lg">
            <KeyRound className="h-4 w-4 text-gray-600" />
          </div>
          <h2 className="text-sm font-semibold text-gray-800">เปลี่ยนรหัสผ่าน</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <input
            className={inputCls}
            type="password"
            placeholder="รหัสผ่านปัจจุบัน"
            autoComplete="current-password"
            value={pw.current}
            onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
          />
          <input
            className={inputCls}
            type="password"
            placeholder="รหัสผ่านใหม่ (8 ตัวขึ้นไป)"
            autoComplete="new-password"
            value={pw.next}
            onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
          />
          <input
            className={inputCls}
            type="password"
            placeholder="ยืนยันรหัสผ่านใหม่"
            autoComplete="new-password"
            value={pw.confirm}
            onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
          />
        </div>
        {pwError && <p className="text-xs text-red-600 mt-2">{pwError}</p>}
        <button
          onClick={() => changePw.mutate()}
          disabled={changePw.isPending || !pw.current || !pw.next || !!pwError || pw.next !== pw.confirm}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {changePw.isPending ? tx('Saving…') : 'เปลี่ยนรหัสผ่าน'}
        </button>
      </div>
    </div>
  )
}

export default AccountPage
